import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Save,
  Loader2,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Minus,
  Plus,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  GOOGLE_FONTS_LIST,
  loadGoogleFont,
} from '@/lib/overlayTemplates';
import {
  renderHeroPreviewToCanvas,
  drawHeroOverlay,
  getDefaultHeroConfig,
} from '@/lib/heroOverlayRenderer';
import type {
  HeroOverlayConfig,
  GalleryImage,
  Project,
  AspectRatio,
} from '@/types';

// ============================================================================
// Props
// ============================================================================

interface HeroOverlayEditorProps {
  image: GalleryImage;
  project: Project;
  onClose: () => void;
  onSave: (imageId: string, heroDataUrl: string, config: HeroOverlayConfig) => Promise<void>;
}

// ============================================================================
// Toggleable Field Component
// ============================================================================

const ToggleableField: React.FC<{
  label: string;
  value: string;
  show: boolean;
  onToggle: () => void;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ label, value, show, onToggle, onChange, placeholder }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={onToggle}
      className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      title={show ? `${label} ausblenden` : `${label} einblenden`}
    >
      {show ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
    </button>
    <div className="flex-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1"
        disabled={!show}
      />
    </div>
  </div>
);

// ============================================================================
// Component
// ============================================================================

export const HeroOverlayEditor: React.FC<HeroOverlayEditorProps> = ({
  image,
  project,
  onClose,
  onSave,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fontButtonRef = useRef<HTMLButtonElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  // Initialize config from project data (with backwards-compat for old configs missing new fields)
  const [config, setConfig] = useState<HeroOverlayConfig>(() => {
    if (image.heroOverlayConfig) {
      const defaults: Pick<HeroOverlayConfig, 'showLocation' | 'showPropertyType' | 'showArea' | 'overlayScale' | 'badgeScale'> = {
        showLocation: true,
        showPropertyType: true,
        showArea: true,
        overlayScale: 2.0,
        badgeScale: 1.0,
      };
      return { ...defaults, ...image.heroOverlayConfig };
    }
    return getDefaultHeroConfig(project);
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Font search state
  const [fontSearch, setFontSearch] = useState('');
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Render trigger for initial mount (dialog animation delay)
  const [renderKey, setRenderKey] = useState(0);

  // Base image URL (prefer staged > empty > original)
  const baseImageUrl = image.stagedUrl || image.emptyUrl || image.originalUrl || '';
  const aspectRatio: AspectRatio = project.aspectRatio || '4:3';

  // Force initial render after dialog mount animation
  useEffect(() => {
    const timer = setTimeout(() => setRenderKey(1), 250);
    return () => clearTimeout(timer);
  }, []);

  // Close font dropdown on outside click
  useEffect(() => {
    if (!fontDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        fontDropdownRef.current && !fontDropdownRef.current.contains(e.target as Node) &&
        fontButtonRef.current && !fontButtonRef.current.contains(e.target as Node)
      ) {
        setFontDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fontDropdownOpen]);

  // Filtered fonts for search
  const filteredFonts = useMemo(() => {
    if (!fontSearch) return GOOGLE_FONTS_LIST;
    const q = fontSearch.toLowerCase();
    return GOOGLE_FONTS_LIST.filter((f) => f.toLowerCase().includes(q));
  }, [fontSearch]);

  // Re-render canvas preview on config change (debounced 100ms)
  useEffect(() => {
    if (!canvasRef.current || !baseImageUrl) return;

    const timeout = setTimeout(async () => {
      try {
        await renderHeroPreviewToCanvas(baseImageUrl, config, aspectRatio, canvasRef.current!);
      } catch (error) {
        console.error('[HeroEditor] Preview render error:', error);
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [config, baseImageUrl, aspectRatio, renderKey]);

  const updateConfig = useCallback((updates: Partial<HeroOverlayConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
    setSaveSuccess(false);
  }, []);

  // Font selection
  const handleFontSelect = useCallback(
    async (font: string) => {
      await loadGoogleFont(font);
      updateConfig({ fontFamily: font });
      setFontDropdownOpen(false);
      setFontSearch('');
    },
    [updateConfig]
  );

  // Open font dropdown with position calculation
  const openFontDropdown = useCallback(() => {
    if (fontButtonRef.current) {
      const rect = fontButtonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setFontDropdownOpen((prev) => !prev);
  }, []);

  // Save: render full-res and pass to parent
  const handleSave = useCallback(async () => {
    if (!baseImageUrl) return;

    setIsSaving(true);
    try {
      const dataUrl = await drawHeroOverlay(baseImageUrl, config, aspectRatio);
      await onSave(image.id, dataUrl, config);
      setSaveSuccess(true);
      setTimeout(() => onClose(), 800);
    } catch (error) {
      console.error('[HeroEditor] Save error:', error);
    } finally {
      setIsSaving(false);
    }
  }, [baseImageUrl, config, aspectRatio, image.id, onSave, onClose]);

  const overlayScalePercent = Math.round((config.overlayScale ?? 2.0) * 100);
  const badgeScalePercent = Math.round((config.badgeScale ?? 1.0) * 100);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      {/* Scrolling lives in an inner child: making the transformed (translate-centered),
          rounded DialogContent itself the scroller causes white compositing artifacts
          at the edges during fast momentum scrolls (same pattern as UpdateDetailModal). */}
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-0">
          <DialogTitle>Titelbild bearbeiten</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto overscroll-contain grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 p-6 pt-8">
          {/* Left Column: Controls */}
          <div className="space-y-4 overflow-y-auto max-h-[70vh] pr-1">
            {/* Badge */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Badge
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Badge anzeigen</Label>
                  <Switch
                    checked={config.badgeEnabled}
                    onCheckedChange={(checked) => updateConfig({ badgeEnabled: checked })}
                  />
                </div>
                {config.badgeEnabled && (
                  <>
                    <Input
                      value={config.badgeText}
                      onChange={(e) => updateConfig({ badgeText: e.target.value })}
                      placeholder="z.B. NEU IM VERKAUF"
                    />
                    {/* Badge Scale Slider */}
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Badge-Gr\u00f6\u00dfe: {badgeScalePercent}%
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            updateConfig({
                              badgeScale: Math.max(0.5, Math.round(((config.badgeScale ?? 1.0) - 0.1) * 10) / 10),
                            })
                          }
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <input
                          type="range"
                          min="0.5"
                          max="2.0"
                          step="0.1"
                          value={config.badgeScale ?? 1.0}
                          onChange={(e) => updateConfig({ badgeScale: parseFloat(e.target.value) })}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            updateConfig({
                              badgeScale: Math.min(2.0, Math.round(((config.badgeScale ?? 1.0) + 0.1) * 10) / 10),
                            })
                          }
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Property Data — all fields toggleable */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Immobiliendaten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ToggleableField
                  label="Standort"
                  value={config.location}
                  show={config.showLocation}
                  onToggle={() => updateConfig({ showLocation: !config.showLocation })}
                  onChange={(v) => updateConfig({ location: v })}
                  placeholder="z.B. Leipzig"
                />
                <ToggleableField
                  label="Objekttyp"
                  value={config.propertyType}
                  show={config.showPropertyType}
                  onToggle={() => updateConfig({ showPropertyType: !config.showPropertyType })}
                  onChange={(v) => updateConfig({ propertyType: v })}
                  placeholder="z.B. Eigentumswohnung"
                />
                <ToggleableField
                  label="Fl\u00e4che"
                  value={config.area}
                  show={config.showArea}
                  onToggle={() => updateConfig({ showArea: !config.showArea })}
                  onChange={(v) => updateConfig({ area: v })}
                  placeholder="z.B. 118 m\u00b2"
                />
                <ToggleableField
                  label="Zimmer"
                  value={config.rooms}
                  show={config.showRooms}
                  onToggle={() => updateConfig({ showRooms: !config.showRooms })}
                  onChange={(v) => updateConfig({ rooms: v })}
                  placeholder="z.B. 3,5 Zimmer"
                />
                <ToggleableField
                  label="Preis"
                  value={config.price}
                  show={config.showPrice}
                  onToggle={() => updateConfig({ showPrice: !config.showPrice })}
                  onChange={(v) => updateConfig({ price: v })}
                  placeholder="z.B. 485.000 EUR"
                />
              </CardContent>
            </Card>

            {/* Accent Color */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Akzentfarbe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={config.accentColor}
                    onChange={(e) => updateConfig({ accentColor: e.target.value })}
                    className="hero-color-picker w-9 h-9 rounded-lg border border-border cursor-pointer"
                  />
                  <Input
                    value={config.accentColor}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                        updateConfig({ accentColor: val });
                      }
                    }}
                    className="w-24 font-mono text-sm"
                    placeholder="#1a365d"
                  />
                  <div className="flex gap-1.5">
                    {['#1a365d', '#1e3a2f', '#4a1942', '#8b2500', '#2d3748', '#000000'].map(
                      (color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => updateConfig({ accentColor: color })}
                          className={`w-6 h-6 rounded-full border-2 transition-all ${
                            config.accentColor === color
                              ? 'border-primary scale-110'
                              : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      )
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Overlay Scale */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Overlay-Gr\u00f6\u00dfe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Label className="text-xs text-muted-foreground">
                  Gr\u00f6\u00dfe: {overlayScalePercent}%
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() =>
                      updateConfig({
                        overlayScale: Math.max(0.5, Math.round(((config.overlayScale ?? 2.0) - 0.1) * 10) / 10),
                      })
                    }
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <input
                    type="range"
                    min="0.5"
                    max="4.0"
                    step="0.1"
                    value={config.overlayScale ?? 2.0}
                    onChange={(e) => updateConfig({ overlayScale: parseFloat(e.target.value) })}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() =>
                      updateConfig({
                        overlayScale: Math.min(4.0, Math.round(((config.overlayScale ?? 2.0) + 0.1) * 10) / 10),
                      })
                    }
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Font Selection */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Schriftart
                </CardTitle>
              </CardHeader>
              <CardContent>
                <button
                  ref={fontButtonRef}
                  type="button"
                  onClick={openFontDropdown}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-input bg-background text-sm hover:bg-accent transition-colors"
                >
                  <span style={{ fontFamily: `'${config.fontFamily}', sans-serif` }}>
                    {config.fontFamily}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-muted-foreground transition-transform ${
                      fontDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={isSaving || saveSuccess}
              className="w-full gap-2"
              size="lg"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Wird gespeichert...
                </>
              ) : saveSuccess ? (
                <>
                  <Check className="w-5 h-5" /> Gespeichert!
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" /> Titelbild speichern
                </>
              )}
            </Button>
          </div>

          {/* Right Column: Live Preview */}
          <div className="lg:sticky lg:top-0 self-start">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Vorschau
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative rounded-lg overflow-hidden bg-muted">
                  <canvas
                    ref={canvasRef}
                    className="w-full block"
                    style={{ aspectRatio: aspectRatio === '1:1' ? '1/1' : '4/3' }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>

      {/* Font Dropdown Portal (rendered outside dialog to avoid overflow clipping) */}
      {fontDropdownOpen && createPortal(
        <div
          ref={fontDropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 9999,
          }}
          className="bg-popover border border-border rounded-lg shadow-lg"
        >
          <div className="p-2">
            <Input
              value={fontSearch}
              onChange={(e) => setFontSearch(e.target.value)}
              placeholder="Schriftart suchen..."
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto px-1 pb-1">
            {filteredFonts.slice(0, 60).map((font) => (
              <button
                key={font}
                type="button"
                onClick={() => handleFontSelect(font)}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded text-sm text-left transition-colors ${
                  config.fontFamily === font
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-accent'
                }`}
              >
                <span>{font}</span>
                {config.fontFamily === font && (
                  <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                )}
              </button>
            ))}
            {filteredFonts.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">
                Keine Schriftart gefunden
              </p>
            )}
          </div>
        </div>,
        document.body
      )}
    </Dialog>
  );
};
